<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("admin_schedules_list.delete_fights") }}
        </template>
        <template v-slot:body>
            <div class="columns is-multiline" v-if="initDone">
                <DeleteScopeSelector />
            </div>
            <div v-else class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>

                <ConfirmButton
                    v-if="store.mode !== DeleteFightsMode.Category"
                    ref="deleteFightsButton"
                    :disabled="!store.canDeleteScope"
                    first-class="button is-danger"
                    second-class="button is-danger"
                    @click="deleteFights"
                >
                    <template v-slot:firstClick>
                        <span class="icon"><i class="fa fa-trash-alt"></i></span>
                        <span>{{ translate("admin_schedules_list.delete.delete_n_fights", false, [store.totalFightsInScope]) }}</span>
                    </template>
                    <template v-slot:secondClick>
                        <span class="icon"><i class="fa fa-exclamation-triangle"></i></span>
                        <span>{{ translate("common.are_you_sure") }}</span>
                    </template>
                </ConfirmButton>
                <ConfirmButton
                    v-else
                    ref="deleteCategoriesButton"
                    :disabled="store.selectedCategoryIds.length === 0"
                    first-class="button is-danger"
                    second-class="button is-danger"
                    @click="deleteSelectedCategories"
                >
                    <template v-slot:firstClick>
                        <span class="icon"><i class="fa fa-trash-alt"></i></span>
                        <span>{{ translate("admin_schedules_list.delete.delete_categories") }}</span>
                    </template>
                    <template v-slot:secondClick>
                        <span class="icon"><i class="fa fa-exclamation-triangle"></i></span>
                        <span>{{ translate("common.are_you_sure") }}</span>
                    </template>
                </ConfirmButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import DeleteScopeSelector from "./DeleteScopeSelector.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import ConfirmButton from "../../Common/ConfirmButton.vue";
    import { handleError } from "../../Common/errorHandler";
    import { translate } from "../../Common/translations";
    import { successToast } from "../../Common/toasts";
    import { DeleteFightsMode, useDeletingFightsStore } from "./useDeletingFightsStore";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, open: openModal, close } = useModal();
    const store = useDeletingFightsStore();
    const initDone = ref(false);
    const deleteFightsButton = ref<InstanceType<typeof ConfirmButton> | null>(null);
    const deleteCategoriesButton = ref<InstanceType<typeof ConfirmButton> | null>(null);

    store.setEventId(props.eventId);

    function open(): Promise<void> {
        store.clearState();
        initDone.value = false;
        deleteFightsButton.value?.reset();
        deleteCategoriesButton.value?.reset();
        openModal();

        return store
            .reloadStatuses()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function deleteFights() {
        return store
            .deleteFights()
            .then(() => {
                successToast(translate("admin_schedules_list.delete.fights_deleted"));
                close();
            })
            .catch(error => {
                deleteFightsButton.value?.reset();
                handleError(error);
            });
    }

    function deleteSelectedCategories() {
        return store
            .deleteSelectedCategories()
            .then(() => {
                successToast(translate("admin_schedules_list.fights_by_category_deleted"));
                deleteCategoriesButton.value?.reset();
            })
            .catch(error => {
                deleteCategoriesButton.value?.reset();
                handleError(error);
            });
    }

    defineExpose({ open });
</script>
